import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cn } from '@repo/lib/cn';
import { cva } from 'class-variance-authority';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button/Button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator/Separator';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet/Sheet';
import { Skeleton } from '@/components/ui/skeleton/Skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip/Tooltip';
import { useLocalStorage } from '@/hooks/local-storage';
import { useMediaQuery } from '@/hooks/media-query';

const SIDEBAR_WIDTH = '14rem';
const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_ICON = '4.75rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

interface SidebarContext {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
}

const SidebarContext = createContext<SidebarContext>({
  state: 'expanded',
  open: true,
  setOpen: () => {},
  openMobile: false,
  setOpenMobile: () => {},
  isMobile: false,
  toggleSidebar: () => {},
});

export function useSidebar() {
  const context = use(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }

  return context;
}

export function SidebarProvider(
  sidebarProviderProps: ComponentProps<'div'> & {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
  }
) {
  const { defaultOpen = true, open: openProp, onOpenChange: setOpenProp, className, style, children, ...props } = sidebarProviderProps;

  const isMobile = !useMediaQuery('md');
  const [openMobile, setOpenMobile] = useState<boolean>(false);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = useLocalStorage('sidebar_state', defaultOpen);
  const open = openProp ?? _open;
  const setOpen = useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
    },
    [open, setOpenProp, _setOpen]
  );

  // Helper to toggle the sidebar.
  const toggleSidebar = useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);

  // Adds a keyboard shortcut to toggle the sidebar.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? 'expanded' : 'collapsed';

  const contextValue = useMemo<SidebarContext>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar]
  );

  return (
    <SidebarContext value={contextValue}>
      <TooltipProvider delayDuration={50}>
        <div
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH,
              '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn('group/sidebar-wrapper has-data-[variant=inset]:bg-background flex min-h-svh w-full', className)}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext>
  );
}

export function Sidebar(
  sidebarProps: ComponentProps<'div'> & {
    side?: 'left' | 'right';
    variant?: 'sidebar' | 'floating' | 'inset';
    collapsible?: 'offcanvas' | 'icon' | 'none';
  }
) {
  const { side = 'left', variant = 'sidebar', collapsible = 'offcanvas', className, children, ...props } = sidebarProps;
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
  if (collapsible === 'none') {
    return (
      <div data-slot="sidebar" className={cn('bg-background flex h-full w-(--sidebar-width) flex-col', className)} {...props}>
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-slot="sidebar"
          data-sidebar="sidebar"
          data-mobile="true"
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH_MOBILE,
            } as React.CSSProperties
          }
          side={side}
          className="bg-background border-border-primary w-(--sidebar-width) p-0 [&>button]:hidden"
        >
          <SheetTitle className={'sr-only'}>Panel boczny</SheetTitle>
          <SheetDescription className={'sr-only'}>Panel nawigacyjny aplikacji, zawiera odnośniki do głównych sekcji.</SheetDescription>
          <div className="flex h-full w-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className="group peer hidden md:block" data-state={state} data-collapsible={state === 'collapsed' ? collapsible : ''} data-variant={variant} data-side={side} data-slot="sidebar">
      {/* This is what handles the sidebar gap on desktop */}
      <div
        className={cn(
          'relative h-svh w-(--sidebar-width) overflow-hidden bg-transparent transition-[width] duration-150 ease-linear',
          'group-data-[collapsible=offcanvas]:w-0',
          'group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))-2px)]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)'
        )}
      />
      <div
        className={cn(
          'border-border-primary fixed inset-y-0 z-10 hidden h-svh w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex',
          side === 'left' ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]' : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
          // Adjust the padding for floating and inset variants.
          variant === 'floating' || variant === 'inset'
            ? 'py-4 pl-4 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))-2px)]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l',
          className
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          className="bg-background group-data-[variant=floating]:border-border-primary relative flex h-full w-full flex-col overflow-hidden group-data-[variant=floating]:rounded-2xl group-data-[variant=floating]:border"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export function SidebarTrigger(sidebarTriggerProps: ComponentProps<typeof Button>) {
  const { onClick, ...props } = sidebarTriggerProps;
  const { toggleSidebar } = useSidebar();
  return (
    <Button
      data-slot="sidebar-trigger"
      data-sidebar="trigger"
      variant="ghost"
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    />
  );
}

export function SidebarRail(sidebarRailProps: ComponentProps<'button'>) {
  const { className, ...props } = sidebarRailProps;

  const { toggleSidebar } = useSidebar();

  return (
    <button
      data-slot="sidebar-rail"
      data-sidebar="rail"
      aria-label="Toggle Sidebar"
      tabIndex={-1}
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        'hover:after:bg-border-primary-focus absolute inset-y-0 z-40 hidden w-4 -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex',
        '[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize',
        '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'group-data-[collapsible=offcanvas]:hover:bg-background group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className
      )}
      {...props}
    />
  );
}

export function SidebarInset(sidebarInsetProps: ComponentProps<'main'>) {
  const { className, ...props } = sidebarInsetProps;
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        'relative flex min-h-svh flex-1 flex-col',
        'peer-data-[variant=inset]:min-h-[calc(100svh-theme(spacing.4))] md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-2xl md:peer-data-[variant=inset]:shadow md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2',
        className
      )}
      {...props}
    />
  );
}

export function SidebarInput(sidebarInputProps: ComponentProps<typeof Input>) {
  const { className, ...props } = sidebarInputProps;

  return <Input data-slot="sidebar-input" data-sidebar="input" className={cn('x w-full', className)} {...props} />;
}

export function SidebarHeader(sidebarHeaderProps: ComponentProps<'div'>) {
  const { className, ...props } = sidebarHeaderProps;

  return <div data-slot="sidebar-header" data-sidebar="header" className={cn('flex flex-col gap-2 p-4', className)} {...props} />;
}

export function SidebarFooter(sidebarFooterProps: ComponentProps<'div'>) {
  const { className, ...props } = sidebarFooterProps;

  return <div data-slot="sidebar-footer" data-sidebar="footer" className={cn('flex flex-col gap-2 p-2', className)} {...props} />;
}

export function SidebarSeparator(sidebarSeparatorProps: ComponentProps<typeof Separator>) {
  const { className, ...props } = sidebarSeparatorProps;
  return <Separator data-slot="sidebar-separator" data-sidebar="separator" className={cn('bg-border-primary mx-2 w-auto', className)} {...props} />;
}

export function SidebarContent(sidebarContentProps: ComponentProps<'div'>) {
  const { className, children, ...props } = sidebarContentProps;
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn('border-border-primary flex min-h-0 flex-1 flex-col gap-2 group-data-[collapsible=icon]:overflow-hidden', className)}
      {...props}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto">{children}</div>
    </div>
  );
}

export function SidebarGroup(sidebarGroupProps: ComponentProps<'div'>) {
  const { className, ...props } = sidebarGroupProps;

  return <div data-slot="sidebar-group" data-sidebar="group" className={cn('relative flex w-full min-w-0 flex-col p-4', className)} {...props} />;
}

export function SidebarGroupLabel(sidebarGroupLabelProps: ComponentProps<'div'> & { asChild?: boolean }) {
  const { className, asChild = false, ...props } = sidebarGroupLabelProps;

  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        'text-accent-foreground/85 flex h-8 items-center rounded-sm px-2 text-xs font-medium transition-[margin,opacity,width] outline-none disabled:opacity-75 [&_svg]:pointer-events-none [&_svg]:size-4',
        'w-full group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:opacity-0',
        className
      )}
      {...props}
    />
  );
}

export function SidebarGroupAction(sidebarGroupActionProps: ComponentProps<'button'> & { asChild?: boolean }) {
  const { className, asChild = false, ...props } = sidebarGroupActionProps;
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        'hover:bg-accent hover:text-accent-foreground outline-focus-ring absolute top-3.5 right-3 flex aspect-square w-5 cursor-pointer items-center justify-center rounded-sm p-0 outline-0 transition-transform focus-visible:outline-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-75 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 after:md:hidden',
        'group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  );
}

export function SidebarGroupContent(sidebarGroupContentProps: ComponentProps<'div'>) {
  const { className, ...props } = sidebarGroupContentProps;
  return <div data-slot="sidebar-group-content" data-sidebar="group-content" className={cn('w-full', className)} {...props} />;
}

export function SidebarMenu(sidebarMenuProps: ComponentProps<'ul'>) {
  const { className, ...props } = sidebarMenuProps;

  return <ul data-slot="sidebar-menu" data-sidebar="menu" className={cn('flex w-full min-w-0 flex-col gap-1', className)} {...props} />;
}

export function SidebarMenuItem(sidebarMenuItemProps: ComponentProps<'li'>) {
  const { className, ...props } = sidebarMenuItemProps;

  return <li data-slot="sidebar-menu-item" data-sidebar="menu-item" className={cn('group/menu-item relative', className)} {...props} />;
}

const sidebarMenuButtonVariants = cva(
  'peer/menu-button hover:bg-card/25 focus-visible:bg-card/75 text-accent-foreground/60 hover:text-accent-foreground/80 data-[active=true]:text-foreground aria-[current=page]:text-foreground data-[active=true]shadow-sm data-[active=true]:bg-accent/45 aria-[current=page]:bg-accent/45 dark:data-[active=true]:bg-card/75 dark:aria-[current=page]:bg-card/75 flex w-full cursor-pointer items-center gap-2 truncate overflow-hidden rounded-sm px-3 py-1 text-left text-sm whitespace-nowrap transition-all duration-150 group-has-[[data-sidebar=menu-action]]/menu-item:pr-8 group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:gap-3 group-data-[collapsible=icon]:!p-3 focus-visible:shadow-sm disabled:pointer-events-none disabled:opacity-60 aria-disabled:pointer-events-none aria-disabled:opacity-60 aria-[current=page]:shadow-sm data-[active=true]:font-medium [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: '',
        outline: 'border-border-secondary border',
      },
      size: {
        md: 'text-md h-9',
        sm: 'h-8 text-sm',
        lg: 'text-md h-12',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'sm',
    },
  }
);

export function SidebarMenuButton(
  sidebarMenuButtonProps: ComponentProps<'button'> & {
    asChild?: boolean;
    isActive?: boolean;
    tooltip?: string | ComponentProps<typeof TooltipContent>;
  } & VariantProps<typeof sidebarMenuButtonVariants>
) {
  const { asChild = false, isActive = false, variant, size, tooltip, className, ...props } = sidebarMenuButtonProps;

  const Comp = asChild ? Slot : 'button';
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp data-slot="sidebar-menu-button" data-sidebar="menu-button" data-size={size} data-active={isActive} className={cn(sidebarMenuButtonVariants({ variant, size }), className)} {...props} />
  );

  if (!tooltip) {
    return button;
  }

  const tooltipProps = typeof tooltip === 'string' ? { children: tooltip } : tooltip;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" align="center" hidden={state !== 'collapsed' || isMobile} {...tooltipProps} />
    </Tooltip>
  );
}

export function SidebarMenuAction(
  sidebarMenuActionProps: ComponentProps<'button'> & {
    asChild?: boolean;
    showOnHover?: boolean;
  }
) {
  const { className, asChild = false, showOnHover = false, ...props } = sidebarMenuActionProps;
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        'hover:bg-accent/80 peer-hover/menu-button:text-foreground outline-focus-ring absolute top-1.5 right-1 flex aspect-square w-5 cursor-pointer items-center justify-center rounded-sm p-0 outline-0 transition-transform outline-none focus-visible:outline-3 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 after:md:hidden',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        showOnHover && 'peer-data-[active=true]/menu-button:text-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0',
        className
      )}
      {...props}
    />
  );
}

export function SidebarMenuBadge(sidebarMenuBadgeProps: ComponentProps<'div'>) {
  const { className, ...props } = sidebarMenuBadgeProps;
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        'pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-sm px-1 text-xs font-medium tabular-nums select-none',
        'peer-hover/menu-button:text-foreground peer-data-[active=true]/menu-button:text-foreground',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        className
      )}
      {...props}
    />
  );
}

export function SidebarMenuSkeleton(
  sidebarMenuSkeletonProps: ComponentProps<'div'> & {
    showIcon?: boolean;
  }
) {
  const { className, showIcon = false, ...props } = sidebarMenuSkeletonProps;
  const width = useMemo<string>(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  return (
    <div data-slot="sidebar-menu-skeleton" data-sidebar="menu-skeleton" className={cn('flex h-8 items-center gap-2 rounded-sm px-2', className)} {...props}>
      {showIcon && <Skeleton className="size-4 rounded-md" data-sidebar="menu-skeleton-icon" />}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

export function SidebarMenuSub(sidebarMenuSubProps: ComponentProps<'ul'>) {
  const { className, ...props } = sidebarMenuSubProps;
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn('border-border-primary ms-3 flex min-w-0 translate-x-px flex-col gap-1 border-l px-2 py-0.5', 'group-data-[collapsible=icon]:hidden', className)}
      {...props}
    />
  );
}

export function SidebarMenuSubItem(sidebarMenuSubItemProps: ComponentProps<'li'>) {
  const { className, ...props } = sidebarMenuSubItemProps;
  return <li data-slot="sidebar-menu-sub-item" data-sidebar="menu-sub-item" className={cn('group/menu-sub-item relative', className)} {...props} />;
}

export function SidebarMenuSubButton(
  sidebarMenuSubButtonProps: ComponentProps<'a'> & {
    asChild?: boolean;
    isActive?: boolean;
  } & VariantProps<typeof sidebarMenuButtonVariants>
) {
  const { asChild = false, isActive = false, variant, size, className, ...props } = sidebarMenuSubButtonProps;

  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
